import {
  Feature,
  FeatureFlag,
  Features,
  FEATURES,
  isFeatureFlag,
  ViewerTool,
} from "@/store/features/features";
import { FaroText, NO_TRANSLATE_CLASS, SearchBar } from "@faro-lotv/flat-ui";
import { compareDateTimes } from "@faro-lotv/foundation";
import { Link, Stack } from "@mui/material";
import { useCallback, useMemo, useState, type JSX } from "react";
import { DevPanel } from "./dev-panel";

type FeaturesDialogTableProps = {
  /** The tool to show the features for. */
  tool: ViewerTool;
};

function hasTool(e: Feature): e is Feature & Required<Pick<Feature, "tools">> {
  return !!e.tools;
}

/** @returns The main page for the feature flags exploration */
export function FeaturesDialogTable({
  tool,
}: FeaturesDialogTableProps): JSX.Element | null {
  const [searchText, setSearchText] = useState<string>("");
  const resetSearch = useCallback(() => setSearchText(""), []);

  const entries = useMemo<Array<[FeatureFlag, Feature]>>(
    () =>
      Object.keys(Features)
        .filter((key) => isFeatureFlag(key))
        // Only show features that are relevant for the current tool
        .filter((key) => {
          const feature = FEATURES[key];
          const tools = hasTool(feature)
            ? feature.tools
            : [ViewerTool.sphereViewer];
          return tools.includes(tool);
        })
        .sort((a, b) =>
          compareDateTimes(FEATURES[b].createdAt, FEATURES[a].createdAt),
        )
        .map((name) => [name, FEATURES[name]]),
    [tool],
  );

  const filteredEntries = useMemo(
    () =>
      entries.filter(
        ([name, { description }]) =>
          name.toLowerCase().includes(searchText.toLowerCase()) ||
          description.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [entries, searchText],
  );

  return (
    <Stack
      direction="column"
      className={NO_TRANSLATE_CLASS}
      sx={{ gap: 4, width: "100%", height: "100%" }}
    >
      <FaroText variant="bodyM">
        Try out upcoming features and help us improve them! Reach out via the
        Team channel{" "}
        <Link href="https://teams.microsoft.com/l/channel/19%3A90a168c7c10b4236aa27baa450929248%40thread.tacv2/Report%20an%20issue%20(Dev-Stage)?groupId=0863d798-d8f1-47ee-a450-c7d004947fba&tenantId=31a88536-e2ab-44b5-837e-5110158247ab">
          <FaroText variant="hyperLink">
            "Report an issue (Dev-Stage)."
          </FaroText>
        </Link>
      </FaroText>

      <SearchBar
        variant="underlined"
        fullWidth
        text={searchText}
        onTextChanged={setSearchText}
        onSearchReset={resetSearch}
      />
      <DevPanel entries={filteredEntries} searchText={searchText} />
    </Stack>
  );
}
